import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Check, Sparkles, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';
import posthog from '../lib/posthog';
import SiratiPath from './SiratiPath';
import SiratiPlusOffer from './SiratiPlusOffer';
import {
  TIMELINE_OPTIONS, GROWTH_OPTIONS, CHALLENGE_OPTIONS, SUPPORT_OPTIONS, READINESS_OPTIONS,
  READINESS_SAVINGS, goalTypeFromReadiness, readinessFromGoalType,
} from '../lib/onboardingContent';

// Intro box shown above the questions, with a note that answers stay private.
const PrivateNote = () => (
  <span className="flex items-start gap-1.5 mt-2 text-[15px] font-medium">
    <Lock className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
    Only you can see your answers. They appear on your personal dashboard, never in the public community.
  </span>
);

const IntroBox = ({ children, hidePrivateNote }) => (
  <p className="text-base text-gray-900 dark:text-white mb-4 p-3 bg-secondary/10 rounded-arch-lg">
    {children}
    {!hidePrivateNote && <PrivateNote />}
  </p>
);

const OptionButton = ({ value, selected, onClick, children }) => (
  <button
    onClick={() => onClick(value)}
    className={`w-full p-4 rounded-arch-lg text-left transition-all border-2 ${
      selected === value
        ? 'border-primary bg-primary/10 text-primary shadow-md shadow-primary/10'
        : 'border-white/20 bg-white/30 dark:bg-white/5 text-text-main hover:border-primary/30 hover:bg-primary/5'
    }`}
    data-testid={`onboarding-option-${value.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
        selected === value ? 'border-primary bg-primary' : 'border-text-muted/30'
      }`}>
        {selected === value && <Check className="w-3 h-3 text-white" />}
      </div>
      <span className="text-sm font-medium">{children}</span>
    </div>
  </button>
);

const QuestionStep = ({ title, options, selected, onSelect, intro }) => (
  <div>
    {intro}
    <h2 className="font-heading text-xl font-semibold text-text-main mb-6">{title}</h2>
    <div className="space-y-3">
      {options.map(opt => (
        <OptionButton key={opt} value={opt} selected={selected} onClick={onSelect}>{opt}</OptionButton>
      ))}
    </div>
  </div>
);

const AmountField = ({ id, label, hint, value, onChange }) => (
  <div>
    <label className="text-sm font-medium text-text-main mb-1 block" htmlFor={id}>{label}</label>
    {hint && <p className="text-[13px] text-text-muted mb-1">{hint}</p>}
    <input id={id} type="number" min="0" value={value} onChange={e => onChange(e.target.value)}
      placeholder="0" className="w-full px-4 py-3 bg-white/70 dark:bg-white dark:text-gray-800 rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary"
      data-testid={id} />
  </div>
);

const OnboardingFlow = ({ onComplete, existingData }) => {
  const { profile, refreshProfile } = useAuth();
  const [phase, setPhase] = useState('questions'); // questions -> path -> plus
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const [marriageTimeline, setMarriageTimeline] = useState('');
  const [growthFocus, setGrowthFocus] = useState('');
  const [currentChallenge, setCurrentChallenge] = useState('');
  const [desiredSupport, setDesiredSupport] = useState('');
  const [financialReadiness, setFinancialReadiness] = useState('');
  const [financialCurrent, setFinancialCurrent] = useState('');
  const [financialTarget, setFinancialTarget] = useState('');

  const isReview = !!existingData;
  const goalType = goalTypeFromReadiness(financialReadiness);
  // Step 6 (optional dollar amounts) only exists for the savings and debt answers.
  const totalSteps = goalType ? 6 : 5;

  useEffect(() => {
    if (existingData) {
      setMarriageTimeline(existingData.marriage_timeline || '');
      setGrowthFocus(existingData.growth_focus || '');
      setCurrentChallenge(existingData.current_challenge || '');
      setDesiredSupport(existingData.desired_support || '');
      setFinancialReadiness(existingData.financial_readiness || readinessFromGoalType(existingData.financial_goal_type));
      setFinancialCurrent(existingData.financial_current_amount?.toString() || '');
      setFinancialTarget(existingData.financial_target_amount?.toString() || '');
    }
  }, [existingData]);

  const submit = async (withAmounts) => {
    if (!profile?.id) return;
    setSaving(true);
    try {
      const hasAmounts = withAmounts && goalType && financialCurrent !== '' && financialTarget !== '';
      await api.saveOnboarding(profile.id, {
        marriage_timeline: marriageTimeline,
        growth_focus: growthFocus,
        current_challenge: currentChallenge,
        desired_support: desiredSupport,
        financial_readiness: financialReadiness,
        financial_goal_type: goalType,
        financial_current_amount: hasAmounts ? parseFloat(financialCurrent) : null,
        financial_target_amount: hasAmounts ? parseFloat(financialTarget) : null,
      });
      await refreshProfile();
      posthog.capture('profile_completed');
      setPhase('path');
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!marriageTimeline;
      case 2: return !!growthFocus;
      case 3: return !!currentChallenge;
      case 4: return !!desiredSupport;
      case 5: return !!financialReadiness;
      case 6: return financialCurrent !== '' && financialTarget !== '';
      default: return true;
    }
  };

  const handleNext = () => {
    if (step === 5 && !goalType) { submit(false); return; }
    if (step === 6) { submit(true); return; }
    setStep(s => s + 1);
  };

  // After the results screen: new members see the Sirati+ offer; members who
  // are reviewing their answers, or already have Sirati+, go straight on.
  const handlePathContinue = () => {
    if (isReview || profile?.is_premium) { onComplete?.({ paid: false }); return; }
    setPhase('plus');
  };

  const answers = {
    timeline: marriageTimeline,
    growth: growthFocus,
    challenge: currentChallenge,
    support: desiredSupport,
    readiness: financialReadiness,
  };

  const lastStep = step === totalSteps;
  const showSavingsFields = financialReadiness === READINESS_SAVINGS;

  return (
    <div className="min-h-screen feed-ombre-bg flex items-center justify-center p-4 relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-arch-xl max-w-xl w-full p-8 relative z-10"
        data-testid="onboarding-flow"
      >
        {phase === 'questions' && (
          <>
            {/* Progress Bar */}
            <div className="flex items-center gap-1 mb-8">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div key={i} className="flex-1 h-1.5 rounded-full overflow-hidden bg-primary/10">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: i + 1 <= step ? '100%' : '0%',
                      background: 'linear-gradient(90deg, #4AADA8, #A99CC8)',
                    }}
                  />
                </div>
              ))}
            </div>

            <p className="text-xs text-text-muted mb-1">Step {step} of {totalSteps}</p>
          </>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={phase === 'questions' ? `q${step}` : phase}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {phase === 'questions' && step === 1 && (
              <QuestionStep
                intro={<IntroBox>These answers help personalize your dashboard and growth journey. Pick the closest option to where you are right now.</IntroBox>}
                title="When do you ideally hope to get married?"
                options={TIMELINE_OPTIONS} selected={marriageTimeline} onSelect={setMarriageTimeline}
              />
            )}

            {phase === 'questions' && step === 2 && (
              <QuestionStep
                title="What would you most like to feel more confident about before marriage?"
                options={GROWTH_OPTIONS} selected={growthFocus} onSelect={setGrowthFocus}
              />
            )}

            {phase === 'questions' && step === 3 && (
              <QuestionStep
                title="What's the hardest part of preparing for marriage right now?"
                options={CHALLENGE_OPTIONS} selected={currentChallenge} onSelect={setCurrentChallenge}
              />
            )}

            {phase === 'questions' && step === 4 && (
              <QuestionStep
                title="What would be most helpful to you right now?"
                options={SUPPORT_OPTIONS} selected={desiredSupport} onSelect={setDesiredSupport}
              />
            )}

            {phase === 'questions' && step === 5 && (
              <QuestionStep
                intro={<IntroBox>Just a snapshot. There are no wrong answers, and no dollar amounts are needed here.</IntroBox>}
                title="How would you describe your financial readiness for marriage?"
                options={READINESS_OPTIONS} selected={financialReadiness} onSelect={setFinancialReadiness}
              />
            )}

            {phase === 'questions' && step === 6 && (
              <div>
                <p className="flex items-start gap-1.5 text-base font-medium text-gray-900 dark:text-white mb-4 p-3 bg-secondary/10 rounded-arch-lg">
                  <Lock className="w-4 h-4 mt-1 shrink-0" aria-hidden="true" />
                  This is your personal financial goal. It is only visible to you on your dashboard and is never shown to other users.
                </p>
                {showSavingsFields ? (
                  <>
                    <h2 className="font-heading text-xl font-semibold text-text-main mb-2">Want to set a savings goal?</h2>
                    <p className="text-sm text-text-muted mb-6">Enter your current savings and your target for marriage.</p>
                    <div className="space-y-4">
                      <AmountField id="onboarding-current-amount" label="Current savings ($)" value={financialCurrent} onChange={setFinancialCurrent} />
                      <AmountField id="onboarding-target-amount" label="Marriage savings goal ($)" value={financialTarget} onChange={setFinancialTarget} />
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="font-heading text-xl font-semibold text-text-main mb-2">Want to set a debt payoff goal?</h2>
                    <p className="text-sm text-text-muted mb-6">You'll update the remaining balance over time as you pay it down.</p>
                    <div className="space-y-4">
                      <AmountField id="onboarding-target-amount" label="Starting debt ($)" hint="Your full starting debt. This is your reference point and won't change." value={financialTarget} onChange={setFinancialTarget} />
                      <AmountField id="onboarding-current-amount" label="Current balance ($)" hint="What you still owe right now. If you haven't paid any yet, enter the same amount." value={financialCurrent} onChange={setFinancialCurrent} />
                    </div>
                  </>
                )}
                <div className="mt-5 text-center">
                  <button
                    type="button"
                    onClick={() => { setFinancialCurrent(''); setFinancialTarget(''); submit(false); }}
                    disabled={saving}
                    className="min-h-[44px] text-sm text-text-muted hover:text-primary transition-colors underline-offset-2 hover:underline disabled:opacity-50"
                    data-testid="onboarding-skip-financial"
                  >
                    Skip for now. I'll set this up later
                  </button>
                </div>
              </div>
            )}

            {phase === 'path' && (
              <SiratiPath
                answers={answers}
                onContinue={handlePathContinue}
                continueLabel={isReview || profile?.is_premium ? 'Go to my dashboard' : 'Continue'}
              />
            )}

            {phase === 'plus' && (
              <SiratiPlusOffer
                userId={profile?.id}
                onPaid={async () => { await refreshProfile(); onComplete?.({ paid: true }); }}
                onSkip={() => onComplete?.({ paid: false })}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation (questions only) */}
        {phase === 'questions' && (
          <div className="flex items-center justify-between mt-8">
            <button
              onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1}
              className="flex items-center gap-1 px-4 py-2 min-h-[44px] text-text-muted hover:text-text-main transition-colors disabled:opacity-30"
              data-testid="onboarding-back"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Back
            </button>

            {lastStep ? (
              <button
                onClick={handleNext}
                disabled={!canProceed() || saving}
                className="flex items-center gap-2 px-6 py-2.5 min-h-[44px] rounded-full font-medium text-white disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #4AADA8, #A99CC8)' }}
                data-testid="onboarding-submit"
              >
                {saving ? 'Saving...' : (
                  <>{step === 6 ? 'Set goal' : (isReview ? 'Update answers' : 'See my Sirati Path')} <Sparkles className="w-4 h-4" aria-hidden="true" /></>
                )}
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-6 py-2.5 min-h-[44px] bg-primary text-white rounded-full font-medium btn-hover disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="onboarding-next"
              >
                Next <ChevronRight className="w-4 h-4" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default OnboardingFlow;
